import { waPhone } from "@/lib/phone";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  MagnifyingGlass, Phone, MapPin, Bank, Receipt, ArrowUpRight,
  ArrowDownLeft, CurrencyInr, User as UserIcon, WhatsappLogo,
  CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * PARTIES — Vyapar/Tally-style consolidated ledger.
 * Left column: list of every party (grouped by mobile) with outstanding balance.
 * Right column: full transaction history (Sale + Payment-In) with running balance.
 */
export default function PartiesPage({ onOpenOrder }) {
  const [parties, setParties] = useState([]);
  const [q, setQ] = useState("");
  // filterMode: "all" | "due" | "advance" | "cleared"
  // A party's effective balance = order-side balance MINUS any standalone
  // party_credits sitting in `credit_balance`. So a party with ₹0 order
  // balance + ₹11,400 credit_balance shows up as ADVANCE.
  const [filterMode, setFilterMode] = useState("all");
  const [selected, setSelected] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ledgerBusy, setLedgerBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/parties");
      setParties(data.parties || []);
      if (!selected && data.parties?.length) {
        setSelected(data.parties[0]);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  }, [selected]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!selected?.mobile) { setLedger(null); return; }
    let cancelled = false;
    (async () => {
      setLedgerBusy(true);
      try {
        const { data } = await api.get(`/parties/${selected.mobile}/ledger`);
        if (!cancelled) setLedger(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        if (!cancelled) setLedgerBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected?.mobile]);

  // Effective balance for filtering / colour tone. Standalone party credits
  // (customer prepaid without an order) reduce the party's outstanding.
  const effectiveBalance = (p) => Number(p.balance || 0) - Number(p.credit_balance || 0);

  const filtered = useMemo(() => {
    let list = parties;
    if (filterMode === "due") {
      list = list.filter((p) => effectiveBalance(p) > 0.005);
    } else if (filterMode === "advance") {
      list = list.filter((p) => effectiveBalance(p) < -0.005);
    } else if (filterMode === "cleared") {
      list = list.filter((p) => Math.abs(effectiveBalance(p)) <= 0.005);
    }
    if (q.trim()) {
      const ql = q.toLowerCase();
      list = list.filter((p) =>
        (p.customer_name || "").toLowerCase().includes(ql)
        || (p.mobile || "").includes(ql)
        || (p.shop_name || "").toLowerCase().includes(ql));
    }
    return list;
  }, [parties, q, filterMode]);

  // Aggregate metrics — computed over the ORIGINAL parties list (not the
  // filtered slice) so users see the true system-wide totals regardless of
  // which tab they're on.
  const totalDue = parties.reduce((s, p) => {
    const b = effectiveBalance(p);
    return s + (b > 0 ? b : 0);
  }, 0);
  const totalAdvance = parties.reduce((s, p) => {
    const b = effectiveBalance(p);
    return s + (b < 0 ? -b : 0);
  }, 0);
  const dueCount = parties.filter((p) => effectiveBalance(p) > 0.005).length;
  const advanceCount = parties.filter((p) => effectiveBalance(p) < -0.005).length;
  const clearedCount = parties.filter((p) => Math.abs(effectiveBalance(p)) <= 0.005).length;

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-4" data-testid="parties-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// LEDGER</div>
          <h1 className="font-heading text-3xl font-black text-white">Parties</h1>
          <p className="text-slate-400 text-sm mt-1">
            Har customer ka bank-account jaisa hisaab — orders, payments, running balance ek jagah.
          </p>
        </div>
        <div className="flex gap-3">
          <MetricPill label="Total Parties" value={parties.length} tone="cyan" />
          <MetricPill label="Total Due" value={fmt(totalDue)} tone="red" />
          <MetricPill label="Total Advance" value={fmt(totalAdvance)} tone="emerald" />
        </div>
      </div>

      {/* Filter tabs — ALL / DUE / ADVANCE / CLEARED. Owner ne kaha: alag
          se advance panel bhi chahiye aur due bhi. Ek tab row me dono
          filter mil jate hain. */}
      <div className="flex gap-2 flex-wrap" data-testid="party-filter-tabs">
        {[
          { key: "all", label: "ALL", count: parties.length, tone: "cyan" },
          { key: "due", label: "DUE", count: dueCount, tone: "red" },
          { key: "advance", label: "ADVANCE", count: advanceCount, tone: "emerald" },
          { key: "cleared", label: "CLEARED", count: clearedCount, tone: "slate" },
        ].map((t) => {
          const active = filterMode === t.key;
          const toneRing = {
            cyan: active ? "border-cyan-500 bg-cyan-500/15 text-cyan-300" : "border-slate-700 text-slate-400 hover:border-cyan-500/60",
            red: active ? "border-red-500 bg-red-500/15 text-red-300" : "border-slate-700 text-slate-400 hover:border-red-500/60",
            emerald: active ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-slate-700 text-slate-400 hover:border-emerald-500/60",
            slate: active ? "border-slate-400 bg-slate-500/15 text-slate-200" : "border-slate-700 text-slate-400 hover:border-slate-500",
          }[t.tone];
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setFilterMode(t.key)}
              className={
                "px-4 h-9 rounded-sm border-2 font-heading text-xs tracking-widest transition-all inline-flex items-center gap-2 " +
                toneRing
              }
              data-testid={`party-filter-${t.key}`}
            >
              {t.label}
              <span className="font-mono-num text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-800/60 text-slate-300">
                {t.count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-220px)] min-h-[520px]">
        {/* LEFT — party list */}
        <div className="surface rounded-sm overflow-hidden flex flex-col h-full min-h-0">
          <div className="p-3 border-b border-slate-700 relative">
            <MagnifyingGlass className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search Party…"
              className="pl-8 bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm text-sm"
              data-testid="party-search" />
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="text-[10px] font-mono-num tracking-widest text-slate-500 px-3 py-2 border-b border-slate-800 flex justify-between">
              <span>PARTY NAME</span><span>BALANCE</span>
            </div>
            {busy ? (
              <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">Koi party nahi mili.</div>
            ) : (
              filtered.map((p) => {
                const isActive = selected?.mobile === p.mobile;
                const eff = effectiveBalance(p);
                const tone = eff > 0.005 ? "text-red-400" : eff < -0.005 ? "text-emerald-400" : "text-slate-500";
                return (
                  <button
                    key={p.mobile}
                    type="button"
                    onClick={() => setSelected(p)}
                    className={
                      "w-full text-left px-3 py-2.5 border-b border-slate-800 hover:bg-slate-800/40 transition-colors " +
                      (isActive ? "bg-orange-500/10 border-l-2 border-l-orange-500" : "border-l-2 border-l-transparent")
                    }
                    data-testid={`party-row-${p.mobile}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className={"font-semibold truncate " + (isActive ? "text-orange-300" : "text-slate-100")}>
                          {p.customer_name}
                        </div>
                        <div className="text-[10px] text-slate-500 font-mono-num truncate">
                          {p.mobile} · {p.order_count} orders
                        </div>
                        {/* Standalone-advance credit indicator (green chip)
                            — this is the customer's PREPAID balance sitting
                            in party_credits, not yet applied to any order. */}
                        {p.credit_balance > 0 && (
                          <div
                            className="mt-1 inline-block px-1.5 py-0.5 rounded-sm bg-emerald-500/10 border border-emerald-500/40 text-[10px] font-mono-num text-emerald-300 tracking-wider"
                            data-testid={`party-credit-chip-${p.mobile}`}
                          >
                            ↳ Advance {fmt(p.credit_balance)}
                          </div>
                        )}
                      </div>
                      <div className={"font-mono-num text-sm font-bold whitespace-nowrap " + tone}>
                        {eff > 0.005 ? fmt(eff) : (eff < -0.005 ? `+${fmt(Math.abs(eff))}` : "0.00")}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT — party ledger */}
        <div className="surface rounded-sm overflow-hidden flex flex-col h-full min-h-0">
          {!selected ? (
            <div className="p-10 text-center text-slate-500 text-sm">Left se koi party select karo.</div>
          ) : (
            <>
              <div className="p-4 border-b border-slate-700 flex items-start justify-between flex-wrap gap-3">
                <div className="min-w-0">
                  <div className="text-xl font-heading font-black text-slate-100 truncate flex items-center gap-2">
                    <UserIcon size={20} className="text-orange-400" weight="fill" />
                    {ledger?.customer_name || selected.customer_name}
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap text-xs">
                    <a href={`tel:${selected.mobile}`} className="text-cyan-300 font-mono-num flex items-center gap-1 hover:text-cyan-200">
                      <Phone size={12} /> +91 {selected.mobile}
                    </a>
                    {ledger?.address && (
                      <span className="text-slate-400 flex items-center gap-1">
                        <MapPin size={12} /> {ledger.address}
                      </span>
                    )}
                    <a
                      href={`https://wa.me/${waPhone(selected.mobile)}`}
                      target="_blank" rel="noreferrer"
                      className="text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                      data-testid={`party-whatsapp-${selected.mobile}`}
                    >
                      <WhatsappLogo size={14} weight="fill" /> WhatsApp
                    </a>
                  </div>
                </div>
                {ledger && (
                  <div className="flex gap-3 flex-wrap">
                    <SummaryPill label="TOTAL SALES" value={fmt(ledger.total_sales)} tone="cyan" />
                    <SummaryPill label="RECEIVED" value={fmt(ledger.total_paid)} tone="emerald" />
                    {Number(ledger.total_discount || 0) > 0 && (
                      <SummaryPill label="DISCOUNT" value={fmt(ledger.total_discount)} tone="amber" />
                    )}
                    <SummaryPill label="BALANCE" value={fmt(ledger.balance)}
                      tone={ledger.balance > 0 ? "red" : "emerald"} />
                  </div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto">
                <div className="text-[10px] font-mono-num tracking-widest text-slate-400 px-4 py-2 border-b border-slate-700 bg-slate-800/40">
                  <span>TRANSACTIONS ({ledger?.transactions?.length || 0}) — NEWEST FIRST</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/30">
                    <tr>
                      <th className="text-left px-4 py-2">TYPE</th>
                      <th className="text-left px-4 py-2">NUMBER</th>
                      <th className="text-left px-4 py-2">DATE</th>
                      <th className="text-right px-4 py-2">TOTAL</th>
                      <th className="text-right px-4 py-2">BALANCE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerBusy ? (
                      <tr><td colSpan="5" className="p-6 text-center text-slate-500 text-sm">Loading…</td></tr>
                    ) : (ledger?.transactions || []).length === 0 ? (
                      <tr><td colSpan="5" className="p-6 text-center text-slate-500 text-sm">Koi transactions nahi hain.</td></tr>
                    ) : (
                      ledger.transactions.map((t, i) => (
                        <LedgerRow
                          key={i}
                          i={i}
                          t={t}
                          fmt={fmt}
                          onOpenOrder={onOpenOrder}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LedgerRow({ i, t, fmt, onOpenOrder }) {
  const [expanded, setExpanded] = useState(false);
  const isSale = t.type === "Sale";
  const isDiscount = t.type === "Discount";
  const isPayment = t.type === "Payment-In";
  const allocations = Array.isArray(t.allocations) ? t.allocations : [];
  const isMerged = isPayment && !!t.group_id && allocations.length > 1;
  const badgeCls = isSale
    ? "bg-cyan-500/15 text-cyan-300"
    : isDiscount
    ? "bg-amber-500/15 text-amber-300"
    : "bg-emerald-500/15 text-emerald-300";
  const amtCls = isSale
    ? "text-slate-100"
    : isDiscount
    ? "text-amber-300"
    : "text-emerald-400";

  const handleRowClick = () => {
    if ((isSale || isDiscount) && t.order_id) {
      onOpenOrder?.(t.order_id);
    } else if (isMerged) {
      setExpanded((e) => !e);
    } else if (isPayment && !isMerged && t.order_id) {
      onOpenOrder?.(t.order_id);
    }
  };

  const displayNumber = isSale
    ? `#${t.number}`
    : isMerged
    ? `PAY-${(t.group_id || "").toUpperCase()}`
    : isPayment
    ? `#${t.order_number || (t.number || "").slice(0, 8) || "—"}`
    : `#${t.order_number || (t.number || "").slice(0, 8) || "—"}`;

  return (
    <>
      <tr
        className="border-t border-slate-800 hover:bg-slate-800/30 cursor-pointer"
        onClick={handleRowClick}
        data-testid={`ledger-row-${i}`}
      >
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-1.5">
            {isMerged && (
              <CaretRight
                size={11}
                weight="bold"
                className={
                  "text-slate-500 transition-transform " +
                  (expanded ? "rotate-90" : "")
                }
              />
            )}
            <span className={
              "inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono-num font-bold tracking-widest " +
              badgeCls
            }>
              {isSale
                ? <ArrowUpRight size={11} weight="bold" />
                : <ArrowDownLeft size={11} weight="bold" />}
              {t.type}
            </span>
            {isMerged && (
              <span
                className="text-[9px] font-mono-num tracking-wider text-slate-500"
                data-testid={`ledger-row-${i}-alloc-count`}
              >
                {allocations.length} orders
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-2.5 font-mono-num text-orange-300 text-xs">
          {displayNumber}
        </td>
        <td className="px-4 py-2.5 font-mono-num text-slate-400 text-xs">
          {t.date ? new Date(t.date).toLocaleDateString("en-IN") : "—"}
        </td>
        <td
          className={"px-4 py-2.5 text-right font-mono-num font-bold " + amtCls}
          data-testid={`ledger-row-${i}-amount`}
        >
          {isSale ? "" : "− "}{fmt(t.total)}
        </td>
        <td className={
          "px-4 py-2.5 text-right font-mono-num font-bold " +
          (t.balance > 0 ? "text-red-400" : "text-emerald-400")
        }>
          {fmt(t.balance)}
        </td>
      </tr>
      {isMerged && expanded && (
        <tr
          className="bg-slate-900/60 border-t border-slate-800"
          data-testid={`ledger-row-${i}-allocations`}
        >
          <td colSpan={5} className="px-4 py-2">
            <div className="text-[9px] font-mono-num tracking-widest text-slate-500 mb-1.5">
              ALLOCATION BREAKDOWN
            </div>
            <div className="space-y-1">
              {allocations.map((a, ai) => (
                <div
                  key={ai}
                  className="flex items-center justify-between text-xs px-2 py-1 hover:bg-slate-800/40 rounded-sm cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (a.order_id) onOpenOrder?.(a.order_id);
                  }}
                  data-testid={`ledger-row-${i}-alloc-${ai}`}
                >
                  <span className="font-mono-num text-orange-300">
                    #{a.order_number || (a.kind === "advance" ? "ADVANCE" : "—")}
                  </span>
                  <span className="font-mono-num font-bold text-emerald-400">
                    {fmt(a.amount)}
                  </span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function MetricPill({ label, value, tone }) {
  const toneCls = { cyan: "text-cyan-300", emerald: "text-emerald-400", orange: "text-orange-400", red: "text-red-400" }[tone] || "text-slate-100";
  return (
    <div className="surface p-3 rounded-sm min-w-[130px]">
      <div className="text-[10px] font-mono-num tracking-widest text-slate-500">{label.toUpperCase()}</div>
      <div className={"font-mono-num font-black text-xl " + toneCls}>{value}</div>
    </div>
  );
}

function SummaryPill({ label, value, tone }) {
  const toneCls = { cyan: "text-cyan-300", emerald: "text-emerald-400", orange: "text-orange-400", red: "text-red-400", amber: "text-amber-300" }[tone] || "text-slate-100";
  return (
    <div className="px-3 py-1.5 rounded-sm bg-slate-800/50 border border-slate-700">
      <div className="text-[9px] font-mono-num tracking-widest text-slate-500">{label}</div>
      <div className={"font-mono-num font-bold text-sm " + toneCls}>{value}</div>
    </div>
  );
}
